import { useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { format } from 'date-fns'
import { fetchDashboard } from '../api/analytics'
import type { DashboardFilters } from '../types/analytics'
import {
  Activity,
  AlertTriangle,
  MessageSquare,
  Percent,
  ThumbsDown,
  ThumbsUp,
  Users,
} from 'lucide-react'

export default function Dashboard() {
  const { t } = useTranslation()
  const [filters, setFilters] = useState<DashboardFilters>({
    days: 30,
    site_code: undefined,
  })

  // Fetch dashboard stats with TanStack Query
  const { data: stats, isLoading, error } = useQuery({
    queryKey: ['dashboard', filters],
    queryFn: () => fetchDashboard(filters),
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-400">{t('dashboard.loading')}</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-rose-500/10 border border-rose-500/40 rounded-xl p-4">
        <h3 className="text-rose-200 font-medium">{t('dashboard.errorLoading')}</h3>
        <p className="text-rose-300 text-sm mt-1">
          {error instanceof Error ? error.message : t('common.unknownError')}
        </p>
      </div>
    )
  }

  if (!stats) {
    return null
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-semibold text-white">{t('dashboard.title')}</h2>

        {/* Filters */}
        <div className="flex gap-4">
          {/* Site Filter */}
          <select
            value={filters.site_code || ''}
            onChange={(e) =>
              setFilters({ ...filters, site_code: e.target.value || undefined })
            }
            className="px-3 py-2 rounded-md border border-slate-700 bg-surface-200 text-slate-100 focus:ring-2 focus:ring-primary-500/60 focus:border-primary-400/70 transition"
          >
            <option value="">{t('dashboard.filters.allSites')}</option>
            <option value="ua">{t('dashboard.filters.ukraine')}</option>
            <option value="ge">{t('dashboard.filters.georgia')}</option>
            <option value="wp">{t('dashboard.filters.wordpress')}</option>
          </select>

          {/* Days Filter */}
          <select
            value={filters.days}
            onChange={(e) =>
              setFilters({ ...filters, days: parseInt(e.target.value) })
            }
            className="px-3 py-2 rounded-md border border-slate-700 bg-surface-200 text-slate-100 focus:ring-2 focus:ring-primary-500/60 focus:border-primary-400/70 transition"
          >
            <option value="7">{t('dashboard.filters.last7days')}</option>
            <option value="30">{t('dashboard.filters.last30days')}</option>
            <option value="90">{t('dashboard.filters.last90days')}</option>
          </select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title={t('dashboard.stats.totalConversations')}
          value={stats.total_conversations}
          icon={<MessageSquare className="h-8 w-8 text-slate-200" />}
        />
        <StatCard
          title={t('dashboard.stats.uniqueUsers')}
          value={stats.unique_users}
          icon={<Users className="h-8 w-8 text-slate-200" />}
        />
        <StatCard
          title={t('dashboard.stats.totalTokens')}
          value={stats.total_tokens_in + stats.total_tokens_out}
          subtitle={`In: ${stats.total_tokens_in} / Out: ${stats.total_tokens_out}`}
          icon={<Activity className="h-8 w-8 text-slate-200" />}
        />
        <StatCard
          title={t('dashboard.stats.errorRate')}
          value={stats.error_count}
          variant={stats.error_count > 0 ? 'danger' : 'success'}
          icon={<AlertTriangle className="h-8 w-8 text-slate-200" />}
        />
      </div>

      {/* Feedback Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title={t('dashboard.stats.likes')}
          value={stats.total_likes}
          icon={<ThumbsUp className="h-8 w-8 text-slate-200" />}
          variant="success"
        />
        <StatCard
          title={t('dashboard.stats.dislikes')}
          value={stats.total_dislikes}
          icon={<ThumbsDown className="h-8 w-8 text-slate-200" />}
          variant="danger"
        />
        <StatCard
          title={t('dashboard.stats.feedbackRatio')}
          value={`${(stats.like_ratio * 100).toFixed(1)}%`}
          icon={<Percent className="h-8 w-8 text-slate-200" />}
        />
      </div>

      {/* Conversations Timeline Chart */}
      <div className="rounded-xl border border-slate-800 bg-surface-200/80 shadow-lg shadow-black/20 backdrop-blur p-6">
        <h3 className="text-lg font-semibold text-slate-100 mb-4">
          {t('dashboard.charts.conversationsTimeline')}
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={stats.timeseries}>
            <CartesianGrid stroke="rgba(148, 163, 184, 0.15)" strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={(date) => format(new Date(date), 'MMM dd')}
              stroke="#94a3b8"
              tick={{ fill: '#cbd5f5' }}
            />
            <YAxis stroke="#94a3b8" tick={{ fill: '#cbd5f5' }} />
            <Tooltip
              labelFormatter={(date) => format(new Date(date as string), 'MMM dd, yyyy')}
              contentStyle={{
                backgroundColor: '#0f172a',
                borderColor: '#1e293b',
                borderRadius: '0.75rem',
                color: '#e2e8f0',
              }}
              itemStyle={{ color: '#e2e8f0' }}
              labelStyle={{ color: '#94a3b8' }}
            />
            <Legend
              wrapperStyle={{ color: '#cbd5f5' }}
            />
            <Line
              type="monotone"
              dataKey="count"
              stroke="#22d3ee"
              name={t('dashboard.charts.conversations')}
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Token Usage Chart */}
      <div className="rounded-xl border border-slate-800 bg-surface-200/80 shadow-lg shadow-black/20 backdrop-blur p-6">
        <h3 className="text-lg font-semibold text-slate-100 mb-4">
          {t('dashboard.charts.tokenUsage')}
        </h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={stats.timeseries}>
            <CartesianGrid stroke="rgba(148, 163, 184, 0.15)" strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={(date) => format(new Date(date), 'MMM dd')}
              stroke="#94a3b8"
              tick={{ fill: '#cbd5f5' }}
            />
            <YAxis stroke="#94a3b8" tick={{ fill: '#cbd5f5' }} />
            <Tooltip
              labelFormatter={(date) => format(new Date(date as string), 'MMM dd, yyyy')}
              contentStyle={{
                backgroundColor: '#0f172a',
                borderColor: '#1e293b',
                borderRadius: '0.75rem',
                color: '#e2e8f0',
              }}
              itemStyle={{ color: '#e2e8f0' }}
              labelStyle={{ color: '#94a3b8' }}
            />
            <Legend wrapperStyle={{ color: '#cbd5f5' }} />
            <Line
              type="monotone"
              dataKey="tokens_in"
              stroke="#34d399"
              name={t('dashboard.charts.inputTokens')}
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="tokens_out"
              stroke="#f97316"
              name={t('dashboard.charts.outputTokens')}
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Top Countries Chart */}
      {stats.top_countries.length > 0 && (
        <div className="rounded-xl border border-slate-800 bg-surface-200/80 shadow-lg shadow-black/20 backdrop-blur p-6">
          <h3 className="text-lg font-semibold text-slate-100 mb-4">
            {t('dashboard.charts.topCountries')}
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stats.top_countries}>
              <CartesianGrid stroke="rgba(148, 163, 184, 0.15)" strokeDasharray="3 3" />
              <XAxis dataKey="country_code" stroke="#94a3b8" tick={{ fill: '#cbd5f5' }} />
              <YAxis stroke="#94a3b8" tick={{ fill: '#cbd5f5' }} />
              <Tooltip
                labelFormatter={(code) => {
                  const country = stats.top_countries.find(
                    (c) => c.country_code === code
                  )
                  return country?.country_name || code
                }}
                contentStyle={{
                  backgroundColor: '#0f172a',
                  borderColor: '#1e293b',
                  borderRadius: '0.75rem',
                  color: '#e2e8f0',
                }}
                itemStyle={{ color: '#e2e8f0' }}
                labelStyle={{ color: '#94a3b8' }}
              />
              <Legend wrapperStyle={{ color: '#cbd5f5' }} />
              <Bar dataKey="count" fill="#22d3ee" name={t('dashboard.charts.conversations')} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}

// ========================================
// StatCard Component
// ========================================

interface StatCardProps {
  title: string
  value: number | string
  subtitle?: string
  icon?: ReactNode
  variant?: 'default' | 'success' | 'danger'
}

function StatCard({
  title,
  value,
  subtitle,
  icon,
  variant = 'default',
}: StatCardProps) {
  const variantStyles = {
    default:
      'bg-surface-200/80 border border-slate-800 shadow-lg shadow-black/10 backdrop-blur',
    success: 'bg-emerald-500/10 border border-emerald-500/40 shadow-lg shadow-black/10',
    danger: 'bg-rose-500/10 border border-rose-500/40 shadow-lg shadow-black/10',
  }

  return (
    <div className={`rounded-xl p-6 ${variantStyles[variant]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-300">{title}</p>
          <p className="text-3xl font-semibold text-white mt-2">{value}</p>
          {subtitle && (
            <p className="text-xs text-muted-200 mt-1">{subtitle}</p>
          )}
        </div>
        {icon && <div className="flex items-center justify-center">{icon}</div>}
      </div>
    </div>
  )
}
